import { View, Text, Link } from '@react-pdf/renderer';
import { richStyles } from './styles';

/**
 * Renderiza HTML (output de TipTap) como componentes react-pdf.
 *
 * Soporta:
 *   - <p>, <br>
 *   - <strong>/<b>, <em>/<i>, <u>
 *   - <a href="">
 *   - <ul>/<ol> + <li>
 *   - <h1>/<h2>/<h3>
 *   - data-text-align="center|right|justify" (atributo del TipTap TextAlign extension)
 *
 * Uso:
 *   <HtmlRenderer html={cotizacion.comentarios} />
 *
 * IMPLEMENTACION: parse el HTML via DOMParser y recorre el arbol generando
 * un arbol de componentes <View>/<Text>/<Link>. Las marcas (bold, italic, etc.)
 * se acumulan al bajar el arbol.
 */

const HtmlRenderer = ({ html, baseStyle }) => {
    if (!html || typeof html !== 'string') return null;

    const trimmed = html.trim();
    if (!trimmed) return null;

    let root;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${trimmed}</div>`, 'text/html');
        root = doc.body.firstChild;
        if (!root) return null;
    } catch {
        // Fallback: si DOMParser no esta disponible (SSR), renderizar texto plano
        return <Text style={[richStyles.paragraph, baseStyle]}>{trimmed}</Text>;
    }

    return <>{renderChildren(root, { marks: {}, align: null, baseStyle })}</>;
};

// =====================================================
// Render recursivo de nodos
// =====================================================
// `marks` = { bold, italic, underline, link } acumulados desde ancestros
// `align` = 'left' | 'center' | 'right' | 'justify' (heredado del padre)
// `baseStyle` = estilo extra opcional (ej. ajuste de fontSize segun contexto)
// =====================================================

function renderChildren(node, ctx, parentKey = '') {
    const result = [];
    let idx = 0;
    for (const child of Array.from(node.childNodes)) {
        const key = `${parentKey}-${idx++}`;
        const rendered = renderNode(child, ctx, key);
        if (rendered != null) {
            // rendered puede ser array (texto + texto + ...) o un elemento
            if (Array.isArray(rendered)) result.push(...rendered);
            else result.push(rendered);
        }
    }
    return result;
}

function renderNode(node, ctx, key) {
    // ---------- Texto plano ----------
    if (node.nodeType === 3) { // TEXT_NODE
        const text = node.textContent;
        if (!text) return null;
        // Preservar espacios pero no devolver nodos solo-espacios entre bloques
        // (React-PDF maneja whitespace en Text auto)
        return text;
    }

    if (node.nodeType !== 1) return null; // solo procesamos ELEMENT_NODE

    const tag = node.tagName.toLowerCase();
    const align = extractAlign(node) || ctx.align;
    const childCtx = { ...ctx, align };

    // ---------- Bloques de párrafo ----------
    if (tag === 'p') {
        const children = renderChildren(node, childCtx, key);
        if (children.length === 0) {
            // Parrafo vacio -> deja un spacer pequeño
            return <View key={key} style={{ height: 6 }} />;
        }
        // FIX bug @react-pdf/renderer: <Text> con solo <Text> hijos calcula
        // altura 0 → overlap. `flattenSingleTextChild` aplana el estilo del
        // hijo al Text padre cuando es viable; sino, prepende un espacio.
        const { children: runChildren, extraStyle } = flattenSingleTextChild(children);
        return (
            <Text key={key} style={[richStyles.paragraph, alignStyle(align), ctx.baseStyle, extraStyle]}>
                {renderTextRun(runChildren, ctx.marks)}
            </Text>
        );
    }

    // ---------- Headings ----------
    if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
        const headStyle = tag === 'h1' ? richStyles.heading1 : tag === 'h2' ? richStyles.heading2 : richStyles.heading3;
        const children = renderChildren(node, childCtx, key);
        const { children: runChildren, extraStyle } = flattenSingleTextChild(children);
        return (
            <Text key={key} style={[headStyle, alignStyle(align), ctx.baseStyle, extraStyle]}>
                {renderTextRun(runChildren, ctx.marks)}
            </Text>
        );
    }

    // ---------- Saltos de linea ----------
    if (tag === 'br') {
        return '\n';
    }

    // ---------- Listas (con soporte de anidadas) ----------
    // TipTap genera cada <li> asi:
    //   <li><p>texto</p><ul><li>sub</li></ul></li>
    //
    // El <p> interno es un wrapper de TipTap que hay que APLANAR (no envolver
    // como Text propio) porque el listContent ya es un Text — anidar Text dentro
    // de Text rompe la estructura de @react-pdf/renderer.
    //
    // Sublistas (<ul>/<ol>) son bloques (<View>) y van FUERA del Text con
    // indentacion, no dentro (View dentro de Text tampoco esta permitido).
    //
    // Bullets por nivel: usamos solo caracteres seguros que estan en el charset
    // basico latin de Encode Sans / Nunito Sans (Fontsource). Los glyphs mas
    // "bonitos" como ◦ o ▪ no estan en esas fuentes y se muestran como æ.
    //   nivel 0 = "•" (U+2022)   nivel 1 = "–" (en dash)   nivel 2+ = "·" (middle dot)
    // En OL siempre "N."
    if (tag === 'ul' || tag === 'ol') {
        const isOrdered = tag === 'ol';
        const level = ctx.listLevel || 0;
        const isTopLevel = level === 0;
        const bulletsUl = ['•', '–', '·'];
        const items = Array.from(node.children).filter(c => c.tagName.toLowerCase() === 'li');

        // Solo el <ul> top-level agrega marginBottom para separarse del parrafo
        // siguiente. Los uls anidados NO agregan margen (ya viven dentro del
        // <li> padre — sino los niveles se acumulan y se ve muy espaciado).
        const containerStyle = isTopLevel ? { marginBottom: 6 } : null;

        return (
            <View key={key} style={containerStyle}>
                {items.map((li, i) => {
                    const bullet = isOrdered ? `${i + 1}.` : bulletsUl[Math.min(level, bulletsUl.length - 1)];

                    // Separar hijos del <li> en inline vs block (sublistas).
                    // Aplanamos <p> internos que TipTap agrega alrededor del texto.
                    const inlineNodes = [];
                    const blockNodes = [];
                    Array.from(li.childNodes).forEach((child, ci) => {
                        if (child.nodeType === 3) {
                            const t = child.textContent;
                            if (t) inlineNodes.push(t);
                            return;
                        }
                        if (child.nodeType !== 1) return;
                        const childTag = child.tagName.toLowerCase();
                        if (childTag === 'ul' || childTag === 'ol') {
                            blockNodes.push(
                                renderNode(
                                    child,
                                    { ...childCtx, listLevel: level + 1 },
                                    `${key}-li-${i}-sub-${ci}`
                                )
                            );
                        } else if (childTag === 'p') {
                            // Aplanar: usar los children del <p>, no envolverlos.
                            Array.from(child.childNodes).forEach((grand, gi) => {
                                if (grand.nodeType === 3) {
                                    const t = grand.textContent;
                                    if (t) inlineNodes.push(t);
                                } else if (grand.nodeType === 1) {
                                    inlineNodes.push(
                                        renderNode(grand, childCtx, `${key}-li-${i}-p-${ci}-${gi}`)
                                    );
                                }
                            });
                        } else {
                            inlineNodes.push(
                                renderNode(child, childCtx, `${key}-li-${i}-in-${ci}`)
                            );
                        }
                    });

                    // Wrapper del <li> SIN marginBottom propio: el spacing entre
                    // items viene del listItem interno (2pt). Evita acumulacion.
                    // Sublista con marginLeft:12 (indentacion mas suave que 16).
                    return (
                        <View key={`${key}-li-${i}`}>
                            {inlineNodes.length > 0 && (() => {
                                const { children: liChildren, extraStyle } = flattenSingleTextChild(inlineNodes);
                                return (
                                    <View style={richStyles.listItem}>
                                        <Text style={richStyles.listBullet}>{bullet} </Text>
                                        <Text style={[richStyles.listContent, alignStyle(align), ctx.baseStyle, extraStyle]}>
                                            {renderTextRun(liChildren, ctx.marks)}
                                        </Text>
                                    </View>
                                );
                            })()}
                            {blockNodes.length > 0 && (
                                <View style={{ marginLeft: 12 }}>
                                    {blockNodes}
                                </View>
                            )}
                        </View>
                    );
                })}
            </View>
        );
    }

    // ---------- Marcas inline (afectan el texto descendiente) ----------
    // CRITICO: devolver ya envueltos via renderTextRun. Si solo devolvieramos
    // strings, el padre los envolveria con SU marks (perdiendo el bold/italic).
    if (tag === 'strong' || tag === 'b') {
        const innerMarks = { ...ctx.marks, bold: true };
        const children = renderChildren(node, { ...childCtx, marks: innerMarks }, key);
        return renderTextRun(children, innerMarks, key);
    }
    if (tag === 'em' || tag === 'i') {
        const innerMarks = { ...ctx.marks, italic: true };
        const children = renderChildren(node, { ...childCtx, marks: innerMarks }, key);
        return renderTextRun(children, innerMarks, key);
    }
    if (tag === 'u') {
        const innerMarks = { ...ctx.marks, underline: true };
        const children = renderChildren(node, { ...childCtx, marks: innerMarks }, key);
        return renderTextRun(children, innerMarks, key);
    }
    if (tag === 'a') {
        const href = node.getAttribute('href') || '';
        const innerMarks = { ...ctx.marks, link: href };
        const children = renderChildren(node, { ...childCtx, marks: innerMarks }, key);
        return renderTextRun(children, innerMarks, key);
    }

    // ---------- div / span / cualquier otro: recursion transparente ----------
    return renderChildren(node, childCtx, key);
}

/**
 * Aplica las marcas activas a los strings del array.
 * Si hay link, envuelve en <Link>. Si hay otras marcas, en <Text> con estilo.
 *
 * `children` puede contener strings y/o componentes ya emitidos (de marcas anidadas).
 * Los strings se envuelven con las marcas activas; los componentes pasan tal cual
 * (ya traen sus marcas internas aplicadas).
 *
 * `keyPrefix` evita colisiones de React keys cuando esto se llama desde nodos distintos.
 */
function renderTextRun(children, marks, keyPrefix = 'r') {
    return children.map((child, i) => {
        if (typeof child === 'string') {
            return wrapWithMarks(child, marks, `${keyPrefix}-m-${i}`);
        }
        // Ya es un componente (vino de subnodos); lo devolvemos tal cual
        return child;
    });
}

function wrapWithMarks(text, marks, key) {
    const style = [];
    if (marks.bold) style.push(richStyles.bold);
    if (marks.italic) style.push(richStyles.italic);
    if (marks.underline) style.push(richStyles.underline);
    if (marks.link) style.push(richStyles.link);

    if (marks.link) {
        return (
            <Link key={key} src={marks.link} style={style}>
                {text}
            </Link>
        );
    }
    if (style.length === 0) {
        return text;
    }
    return (
        <Text key={key} style={style}>
            {text}
        </Text>
    );
}

/**
 * Fix para bug conocido de @react-pdf/renderer:
 *   Un <Text> que contiene SOLO otros <Text> (o <Link>) sin texto plano
 *   calcula altura 0 → el siguiente bloque se pinta encima (overlap).
 *
 * Casos frecuentes desde TipTap:
 *   <p><strong>Titulo</strong></p>
 *   <p><em>Nota</em></p>
 *
 * Estrategia:
 * 1) Si children === [1 componente Text con string simple dentro], aplanamos
 *    el estilo del hijo al Text padre y devolvemos el string plano. Ya no hay
 *    Text anidado → altura se calcula perfecta y sin espacios extra.
 * 2) Si son varios componentes (ej. <strong>A</strong><em>B</em>) sin texto
 *    plano intercalado, prependemos un espacio como fallback — react-pdf v3
 *    entonces mide bien el bloque.
 * 3) Si ya hay strings mezclados, no toca nada.
 */
function flattenSingleTextChild(children) {
    if (!Array.isArray(children) || children.length === 0) {
        return { children, extraStyle: null };
    }

    // Caso 1: un solo componente Text/Link con children string simple → aplanar.
    if (children.length === 1) {
        const only = children[0];
        if (only && typeof only === 'object' && only.props) {
            const inner = only.props.children;
            if (typeof inner === 'string' || typeof inner === 'number') {
                return {
                    children: [String(inner)],
                    extraStyle: only.props.style || null
                };
            }
        }
    }

    // Caso 2: multiples componentes sin string plano → prepender espacio.
    const hasString = children.some(c => typeof c === 'string');
    if (!hasString) {
        return { children: [' ', ...children], extraStyle: null };
    }

    // Caso 3: mezcla con strings → no tocar.
    return { children, extraStyle: null };
}

function extractAlign(node) {
    const da = node.getAttribute('data-text-align');
    if (da && ['left', 'center', 'right', 'justify'].includes(da)) return da;
    const styleAttr = node.getAttribute('style') || '';
    const m = styleAttr.match(/text-align:\s*(left|center|right|justify)/i);
    if (m) return m[1].toLowerCase();
    return null;
}

function alignStyle(align) {
    if (!align) return null;
    if (align === 'center') return richStyles.alignCenter;
    if (align === 'right') return richStyles.alignRight;
    if (align === 'justify') return richStyles.alignJustify;
    return richStyles.alignLeft;
}

export default HtmlRenderer;
